import type React from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker, Form, Space } from 'antd';
import {
  ECellValueType,
  ExcelTableColDef,
  EExcelTablePaginationType,
} from 'components/ui/excel-table/types';
import { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import ExcelTable from 'components/ui/excel-table';
import usePagination from 'hooks/usePagination';
import { useQuery } from 'react-query';
import {
  claimsApi,
  insuredApi,
  referenceDataApi,
  userApi,
  binderClaimApi,
} from 'services/apis';
import { useNavigate } from 'react-router';
import { RowClickedEvent, SortChangedEvent } from 'ag-grid-community';
import { ENavigationPath } from 'constants/path';
import dayjs from 'dayjs';
import {
  ClaimResponseDtoStatusEnum,
  FilterClaimsFieldEnum,
  FilterClaimsOrderEnum,
  InsuredResponseDtoStatusEnum,
  ClaimPaginationResponseDto,
  BinderClaimPaginationResponseDto,
  ClaimResponseDto,
  BinderClaimResponseDto,
} from 'libs/pinnacle-sdk';
import { LazyLoadingSelect } from 'components/ui/lazy-loading-select';
import FilterButton from 'components/ui/filter-button';
import { EQueryKeys } from 'constants/query-keys';
import { uniqBy } from 'lodash';
import { SortedSelect } from 'components/ui/sorted-select';
import { useDashboardFiltersStore } from 'stores/dashboard-filters';
import { DashboardClaimsTab } from 'locales/en';
import ClaimsTabs from './ClaimsTabs';
import BinderClaimFilters from './BinderFilters';

type ClaimsList = ClaimPaginationResponseDto | BinderClaimPaginationResponseDto;

const MasterClaimsTable: React.FC = () => {
  const { t } = useTranslation();
  const { masterClaimsTable: savedFilters, setMasterClaimsTableFilters } =
    useDashboardFiltersStore();

  const [form] = Form.useForm();
  const [sortField, setSortField] = useState<FilterClaimsFieldEnum | undefined>(
    savedFilters.sortField
  );
  const [sortOrder, setSortOrder] = useState<FilterClaimsOrderEnum | undefined>(
    savedFilters.sortOrder
  );
  const {
    currentPage,
    pageSize,
    onPageChange,
    onShowSizeChange,
    resetPagination,
  } = usePagination();
  const [isFilterOpen, setIsFilterOpen] = useState(savedFilters.isFilterOpen);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<DashboardClaimsTab>(
    DashboardClaimsTab.AllMaster
  );

  const isBinderTab = activeTab === DashboardClaimsTab.AllBinder;

  const insuredOptionsMapRef = useRef<Map<string, string>>(new Map());

  // Restore saved filters to form
  useEffect(() => {
    savedFilters.selectedInsureds?.forEach((insured) => {
      insuredOptionsMapRef.current.set(insured.value, insured.label);
    });

    form.setFieldsValue({
      insureds: savedFilters.selectedInsureds?.map((insured) => insured.value),
      claimsStatus: savedFilters.selectedClaimsStatus,
      lineOfBusiness: savedFilters.selectedLineOfBusiness,
      reportedDateRange: savedFilters.selectedReportedDateRange
        ? [
            dayjs(savedFilters.selectedReportedDateRange[0]),
            dayjs(savedFilters.selectedReportedDateRange[1]),
          ]
        : undefined,
      claimsHandler: savedFilters.selectedClaimsHandler,
    });
  }, []);

  const selectedInsureds = Form.useWatch('insureds', form);
  const selectedClaimsStatus = Form.useWatch('claimsStatus', form);
  const selectedLineOfBusiness = Form.useWatch('lineOfBusiness', form);
  const reportedDateRange = Form.useWatch('reportedDateRange', form);
  const selectedClaimsHandler = Form.useWatch('claimsHandler', form);
  const binderClaimId = Form.useWatch('binderClaimId', form);
  const binderInsuredIds = Form.useWatch('binderInsuredIds', form);
  const binderBrokerNames = Form.useWatch('binderBrokerNames', form);
  const binderReinsuredNames = Form.useWatch('binderReinsuredNames', form);
  const binderNames = Form.useWatch('binderNames', form);
  const binderLineOfBusiness = Form.useWatch('binderLineOfBusiness', form);
  const binderClaimsHandlerIds = Form.useWatch('binderClaimsHandlerIds', form);
  const binderClaimsStatus = Form.useWatch('binderClaimsStatus', form);

  // Save filters to store whenever they change
  useEffect(() => {
    setMasterClaimsTableFilters({
      selectedInsureds:
        selectedInsureds?.map((insuredId: string) => {
          return {
            label: insuredOptionsMapRef.current.get(insuredId) || insuredId,
            value: insuredId,
          };
        }) ?? [],
      selectedClaimsStatus,
      selectedLineOfBusiness,
      selectedReportedDateRange: reportedDateRange
        ? [reportedDateRange[0]?.toDate(), reportedDateRange[1]?.toDate()]
        : undefined,
      selectedClaimsHandler,
      sortField,
      sortOrder,
      isFilterOpen,
    });
  }, [
    selectedInsureds,
    selectedClaimsStatus,
    selectedLineOfBusiness,
    reportedDateRange,
    selectedClaimsHandler,
    sortField,
    sortOrder,
    isFilterOpen,
    setMasterClaimsTableFilters,
  ]);

  useEffect(() => {
    resetPagination();
  }, [
    selectedClaimsStatus,
    selectedLineOfBusiness,
    reportedDateRange,
    selectedClaimsHandler,
    selectedInsureds,
    sortField,
    sortOrder,
    resetPagination,
  ]);

  const { data: list, isLoading: isListLoading } = useQuery<
    ClaimsList,
    unknown,
    ClaimsList,
    string[]
  >({
    queryKey: [
      isBinderTab ? EQueryKeys.BINDER_CLAIMS : EQueryKeys.MASTER_CLAIMS,
      currentPage,
      pageSize,
      isBinderTab ? binderClaimsStatus : selectedClaimsStatus,
      isBinderTab ? binderLineOfBusiness : selectedLineOfBusiness,
      reportedDateRange,
      isBinderTab ? binderClaimsHandlerIds : selectedClaimsHandler,
      isBinderTab ? binderInsuredIds : selectedInsureds,
      isBinderTab ? binderClaimId : undefined,
      isBinderTab ? binderBrokerNames : undefined,
      isBinderTab ? binderReinsuredNames : undefined,
      isBinderTab ? binderNames : undefined,
      sortField,
      sortOrder,
      activeTab,
    ],

    queryFn: () => {
      const insureds = isBinderTab ? binderInsuredIds : selectedInsureds;
      const statuses = isBinderTab ? binderClaimsStatus : selectedClaimsStatus;
      const lineOfBusiness = isBinderTab
        ? binderLineOfBusiness
        : selectedLineOfBusiness;
      const dateReportedFrom = reportedDateRange?.length
        ? reportedDateRange[0]
        : undefined;
      const dateReportedTo = reportedDateRange?.length
        ? reportedDateRange[1]
        : undefined;
      const claimsHandler = isBinderTab
        ? binderClaimsHandlerIds
        : selectedClaimsHandler;

      if (!isBinderTab) {
        return claimsApi.filterClaims({
          page: currentPage,
          pageSize,
          field: sortField,
          order: sortOrder,
          filterClaimsQueryDto: {
            insureds,
            statuses,
            lineOfBusiness,
            dateReportedFrom,
            dateReportedTo,
            claimsHandler,
            includeInsured: true,
            includeClaimHandler: true,
            includeBinderClaims: true,
          },
        });
      }

      return binderClaimApi.filterBinderClaims({
        page: currentPage,
        pageSize,
        field: sortField,
        order: sortOrder,
        filterBinderClaimsQueryDto: {
          insureds,
          statuses,
          claimsHandler,
          dateReportedFrom,
          dateReportedTo,
          includeClaim: true,
          includeContract: true,
          includeClaimHandler: true,
        },
      });
    },
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const { data: businessLines, isLoading: isLoadingBusinessLines } = useQuery(
    [EQueryKeys.BUSINESS_LINES],
    async () => {
      return referenceDataApi.getBusinessLines();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const columnDefs = useMemo<ExcelTableColDef[]>(() => {
    const colDefMaster: ExcelTableColDef[] = [
      {
        colId: FilterClaimsFieldEnum.Insured,
        field: 'insured',
        headerName: t('insured.insured'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
      },
      {
        colId: FilterClaimsFieldEnum.LineOfBusiness,
        field: 'lineOfBusiness',
        headerName: t('submission.lineOfBusiness'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
      },
      {
        colId: FilterClaimsFieldEnum.DateReported,
        field: 'reportedDate',
        headerName: t('masterClaims.reportedDate'),
        minWidth: 180,
        flex: 1,
        resizable: true,
        editable: false,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
      },
      {
        field: 'lossDate',
        headerName: t('masterClaims.lossDate'),
        minWidth: 180,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'incurred',
        headerName: t('masterClaims.incurred'),
        minWidth: 180,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        resizable: true,
        editable: false,
      },
      {
        field: 'payments',
        headerName: t('masterClaims.payments'),
        minWidth: 180,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        resizable: true,
        editable: false,
      },
      {
        field: 'outstanding',
        headerName: t('masterClaims.outstanding'),
        minWidth: 180,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        resizable: true,
        editable: false,
      },
      {
        colId: FilterClaimsFieldEnum.Status,
        field: 'masterClaimsStatus',
        headerName: t('masterClaims.claimsStatus'),
        minWidth: 180,
        flex: 1,
        resizable: true,
        editable: false,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
      },
      {
        colId: FilterClaimsFieldEnum.ClaimsHandler,
        field: 'claimsHandler',
        headerName: t('masterClaims.claimsHandler'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
      },
      {
        field: 'claimId',
        headerName: '',
        width: 0,
        hide: true,
        editable: false,
      },
    ];

    const colDefBinder: ExcelTableColDef[] = [
      {
        field: 'claimId',
        headerName: t('binderClaims.claimsId'),
        minWidth: 150,
        flex: 1,
        hide: false,
        resizable: true,
        editable: false,
      },
      {
        colId: FilterClaimsFieldEnum.Insured,
        field: 'insured',
        headerName: t('insured.insured'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
      },
      {
        field: 'broker',
        headerName: t('masterClaims.broker'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'reinsured',
        headerName: t('masterClaims.reinsured'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'binder',
        headerName: t('masterClaims.binder'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        colId: FilterClaimsFieldEnum.LineOfBusiness,
        field: 'lineOfBusiness',
        headerName: t('submission.lineOfBusiness'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
      },
      {
        field: 'lossDate',
        headerName: t('masterClaims.lossDate'),
        minWidth: 180,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        colId: FilterClaimsFieldEnum.Status,
        field: 'masterClaimsStatus',
        headerName: t('masterClaims.claimsStatus'),
        minWidth: 180,
        flex: 1,
        resizable: true,
        editable: false,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
      },
      {
        field: 'incurred',
        headerName: t('masterClaims.incurred'),
        minWidth: 180,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        resizable: true,
        editable: false,
      },
      {
        field: 'payments',
        headerName: t('masterClaims.payments'),
        minWidth: 180,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        resizable: true,
        editable: false,
      },
      {
        field: 'outstanding',
        headerName: t('masterClaims.outstanding'),
        minWidth: 180,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        resizable: true,
        editable: false,
      },

      {
        colId: FilterClaimsFieldEnum.ClaimsHandler,
        field: 'claimsHandler',
        headerName: t('masterClaims.claimsHandler'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
      },
    ];

    return isBinderTab ? colDefBinder : colDefMaster;
  }, [t, isBinderTab]);

  const rowData = useMemo(() => {
    const items = list?.items || [];
    if (!isBinderTab) {
      const claimItems = items as ClaimResponseDto[];
      return claimItems.map((item: ClaimResponseDto) => ({
        insured: item.insured?.name ?? '',
        lineOfBusiness: item.lineOfBusiness,
        reportedDate: dayjs(item.dateReported).format('DD/MM/YYYY'),
        lossDate: dayjs(item.dateOfLoss).format('DD/MM/YYYY'),
        masterClaimsStatus: item.status,
        incurred: item.incurredOurShare,
        payments: item.paymentsOurShare,
        outstanding: item.outstandingOurShare,
        claimsHandler: item.claimsHandlerUser?.name ?? '',
        claimId: item.claimId,
      }));
    }

    const binderItems = (items as BinderClaimResponseDto[]).filter((item) => {
      if (binderClaimId && !String(item.claimId).includes(binderClaimId))
        return false;
      const insuredId = item?.claim?.insured?.insuredId;
      if (binderInsuredIds?.length && !binderInsuredIds.includes(insuredId))
        return false;
      const brokerName = item?.contract?.broker?.name;
      if (binderBrokerNames?.length && !binderBrokerNames.includes(brokerName))
        return false;
      const reinsuredName = item?.contract?.reinsured?.name;
      if (
        binderReinsuredNames?.length &&
        !binderReinsuredNames.includes(reinsuredName)
      )
        return false;
      const binderName = item.contract?.binder?.binderName;
      if (binderNames?.length && !binderNames.includes(binderName))
        return false;
      if (
        binderLineOfBusiness?.length &&
        !binderLineOfBusiness.includes(item.claim?.lineOfBusiness)
      )
        return false;
      if (
        binderClaimsStatus?.length &&
        !binderClaimsStatus.includes(item.claimStatus)
      )
        return false;
      const handlerId = item?.claim?.claimsHandlerUser?.userId;
      if (
        binderClaimsHandlerIds?.length &&
        !binderClaimsHandlerIds.includes(handlerId)
      )
        return false;
      return true;
    });
    return binderItems.map((item: BinderClaimResponseDto, index: number) => ({
      claimId: item.claimId,
      insured: item?.claim?.insured?.name ?? '',
      broker: item?.contract?.broker?.name ?? '',
      reinsured: item.reinsured?.name ?? item.contract?.reinsured?.name ?? '',
      binder: item.contract?.binder?.binderName ?? '',
      masterClaimsStatus: item.claimStatus,
      lineOfBusiness: item?.claim?.lineOfBusiness,
      lossDate: item?.claim?.dateOfLoss
        ? dayjs(item?.claim?.dateOfLoss).format('DD/MM/YYYY')
        : undefined,
      incurred: item.incurredOurShare,
      payments: item.paymentsOurShare,
      outstanding: item.outstandingOurShare,
      claimsHandler:
        item?.claim?.claimsHandlerUser?.name ??
        item.claimsHandlerUser?.name ??
        '',
      id: pageSize * (currentPage - 1) + index + 1,
    }));
  }, [currentPage, pageSize, list?.items, activeTab]);

  const handleRowClick = useCallback(
    (row: RowClickedEvent) => {
      navigate(
        ENavigationPath.CLAIM_DETAIL.replace(
          ':claimId',
          row.data.claimId
        ).replace(':tabId', '0')
      );
    },
    [navigate]
  );

  const handleSortChanged = useCallback((event: SortChangedEvent) => {
    const sortedCol = event.api.getColumnState().find((col) => col.sort);
    if (sortedCol) {
      setSortField(sortedCol.colId as FilterClaimsFieldEnum);
      setSortOrder(
        sortedCol.sort === 'asc'
          ? FilterClaimsOrderEnum.Asc
          : FilterClaimsOrderEnum.Desc
      );
    } else {
      setSortField(undefined);
      setSortOrder(undefined);
    }
  }, []);

  return (
    <div className="px-3 py-4 bg-white rounded-lg">
      <Form form={form} className="w-full">
        <div className="flex justify-between items-center mb-6">
          <ClaimsTabs activeTab={activeTab} setActiveTab={setActiveTab} />
          <div className="flex gap-2">
            <FilterButton
              isActive={isFilterOpen}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            />
          </div>
        </div>

        {isFilterOpen && activeTab === DashboardClaimsTab.AllMaster && (
          <Space direction="horizontal" wrap>
            <Form.Item name="insureds" className="min-w-[220px] my-2">
              <LazyLoadingSelect
                mode="multiple"
                placeholder={t('insured.selectInsured')}
                fetchOptions={async (page, pageSize, searchText) => {
                  const { items } = await insuredApi.filterInsuredsClaims({
                    page,
                    pageSize,
                    insuredFilterQueryDto: {
                      searchText,
                      statuses: [InsuredResponseDtoStatusEnum.Active],
                    },
                  });
                  const options = items.map((insured) => ({
                    label: insured.name,
                    value: insured.insuredId,
                  }));
                  options.forEach((option) => {
                    insuredOptionsMapRef.current.set(
                      option.value,
                      option.label
                    );
                  });
                  return {
                    data: options,
                  };
                }}
                additionalOptions={savedFilters.selectedInsureds}
                maxTagCount={1}
                size="large"
                allowClear
                queryKey={EQueryKeys.MASTER_CLAIMS_INSURED_LAZY_LOADING_SELECT}
              />
            </Form.Item>

            <Form.Item name="claimsStatus" className="min-w-[220px] my-2">
              <SortedSelect
                mode="multiple"
                placeholder={t('masterClaims.selectClaimsStatus')}
                options={Object.values(ClaimResponseDtoStatusEnum).map(
                  (name) => ({
                    label: name,
                    value: name,
                  })
                )}
                allowClear
                maxTagCount={1}
                showSearch
                size="large"
                className="rounded-xl"
              />
            </Form.Item>

            <Form.Item name="lineOfBusiness" className="min-w-[220px] my-2">
              <SortedSelect
                mode="multiple"
                allowClear
                size="large"
                loading={isLoadingBusinessLines}
                placeholder={t('submission.selectLineOfBusiness')}
                options={uniqBy(businessLines || [], 'name').map(
                  (businessLine) => ({
                    label: businessLine.name,
                    value: businessLine.name,
                  })
                )}
              />
            </Form.Item>

            <Form.Item name="claimsHandler" className="min-w-[220px] my-2">
              <LazyLoadingSelect
                mode="multiple"
                placeholder={t('masterClaims.selectClaimsHandler')}
                fetchOptions={async (page, pageSize, searchText) => {
                  const { items } = await userApi.filterUsers({
                    searchText,
                    page,
                    pageSize,
                  });
                  return {
                    data: items.map((user) => ({
                      label: user.name || '',
                      value: user.userId,
                    })),
                  };
                }}
                additionalOptions={[]}
                allowClear
                maxTagCount={1}
                showSearch
                size="large"
                className="rounded-xl"
                queryKey="claimsHandler-lazy-loading-select"
              />
            </Form.Item>
          </Space>
        )}
        {isFilterOpen && activeTab === DashboardClaimsTab.AllBinder && (
          <BinderClaimFilters
            t={t}
            businessLines={businessLines}
            isLoadingBusinessLines={isLoadingBusinessLines}
          />
        )}
      </Form>
      <ExcelTable
        data={rowData}
        columnDefs={columnDefs}
        onRowClicked={handleRowClick}
        paginationType={EExcelTablePaginationType.DETAIL}
        onPageChange={onPageChange}
        onPageSizeChange={onShowSizeChange}
        onSortChanged={handleSortChanged}
        pageSize={pageSize}
        currentPage={currentPage}
        totalRows={list?.pagination?.total || 0}
        tableContainerClassName="h-[28rem]"
        tableClassName="rounded-t-lg"
        className="mb-2"
        includeIdRow={true}
        loading={isListLoading}
      />
    </div>
  );
};

export default MasterClaimsTable;
